import { el, type Nav, type Screen } from "./dom";
import { buildTopbar } from "./topbar";
import { i18n, t } from "../i18n";
import { ACHIEVEMENTS } from "../game/achievements";
import { progress } from "../store/progress";
import { settings } from "../store/settings";
import { todayKey } from "../game/daily";

export class AchievementsScreen implements Screen {
  private off?: () => void;
  private topRefresh: () => void = () => {};

  constructor(private nav: Nav) {}

  mount(host: HTMLElement): void {
    const screen = el("div", "screen achievements");
    const top = buildTopbar({
      title: "menu.achievements",
      onBack: () => this.nav.home(),
      showStardust: true,
    });
    this.topRefresh = top.refresh;
    const body = el("div", "ach-body");
    screen.append(top.node, body);
    host.appendChild(screen);

    const render = (): void => {
      this.topRefresh();
      body.innerHTML = "";
      body.appendChild(this.buildMissions(render));
      body.appendChild(this.buildAchievements());
    };
    render();
    this.off = i18n.onChange(render);
  }

  /** Daily login reward + rotating missions (recurring stardust). */
  private buildMissions(render: () => void): HTMLElement {
    const day = todayKey();
    const section = el("div", "mission-section");
    section.appendChild(el("div", "mission-head", t("mission.title")));

    // Login reward card.
    const login = progress.loginRewardState(day);
    const card = el("button", `login-card ${login.claimed ? "claimed" : ""}`);
    card.innerHTML = `
      <span class="login-icon">🎁</span>
      <span class="login-info">
        <span class="login-title">${t("mission.login")}</span>
        <span class="login-streak">${t("mission.streak", { n: login.streak })}</span>
      </span>
      <span class="login-action">${
        login.claimed
          ? `<span class="m-done">${t("mission.claimed")}</span>`
          : `<span class="m-reward">+${login.amount} ✦</span>`
      }</span>`;
    card.disabled = login.claimed;
    if (!login.claimed) {
      card.onclick = () => {
        if (progress.claimLogin(day)) render();
      };
    }
    section.appendChild(card);

    // Mission list.
    const list = el("div", "mission-list");
    for (const m of progress.dailyMissions(day, progress.effectiveCols(settings.get("boardCols")))) {
      const pct = Math.min(100, (m.current / m.target) * 100);
      const row = el("div", `mission-row ${m.done ? "done" : ""} ${m.claimed ? "claimed" : ""}`);
      const action = m.claimed
        ? `<span class="m-done">${t("mission.claimed")}</span>`
        : m.done
          ? `<button class="m-claim btn-primary">${t("mission.claim")} +${m.def.reward} ✦</button>`
          : `<span class="m-count">${m.current}/${m.target}</span>`;
      row.innerHTML = `
        <span class="m-icon">${m.def.icon}</span>
        <span class="m-info">
          <span class="m-name">${t(`mission.${m.def.metric}`, { n: m.target })}</span>
          <div class="m-bar"><div class="m-bar-fill" style="width:${pct}%"></div></div>
        </span>
        <span class="m-action">${action}</span>`;
      if (m.done && !m.claimed) {
        const btn = row.querySelector<HTMLButtonElement>(".m-claim");
        if (btn) btn.onclick = () => {
          if (progress.claimMission(day, m.def.id)) render();
        };
      }
      list.appendChild(row);
    }
    section.appendChild(list);
    return section;
  }

  private buildAchievements(): HTMLElement {
    const wrap = el("div", "ach-wrap");
    const owned = ACHIEVEMENTS.filter((a) => progress.hasAchievement(a.id)).length;
    wrap.appendChild(
      el("div", "mission-head", t("menu.achievements")),
    );
    const summary = el("div", "ach-summary");
    summary.textContent = t("ach.progress", { n: owned, total: ACHIEVEMENTS.length });
    wrap.appendChild(summary);

    const list = el("div", "ach-list");
    for (const def of ACHIEVEMENTS) {
      const got = progress.hasAchievement(def.id);
      const row = el("div", `ach-row ${got ? "got" : "locked"}`);
      row.innerHTML = `
        <span class="ach-icon">${got ? def.icon : "🔒"}</span>
        <span class="ach-info">
          <span class="ach-name">${t(`ach.${def.id}`)}</span>
          <span class="ach-desc">${t(`ach.${def.id}.desc`)}</span>
        </span>
        <span class="ach-reward">+${def.reward} ✦</span>`;
      list.appendChild(row);
    }
    wrap.appendChild(list);
    return wrap;
  }

  unmount(): void {
    this.off?.();
  }
}
